import type { ZudokuConfig } from "zudoku";
import { McpHero } from "./src/components/McpHero";
import { Recipe } from "./src/components/Recipe";
import {
  SkillOverview,
  SkillPrompts,
  SkillServers,
  SkillsLibrary,
} from "./src/components/SkillsLibrary";
import skills from "./src/generated/skills.json";
import "./src/styles.css";

const config: ZudokuConfig = {
  site: {
    title: "SecuriThings Developer Center",
    logo: {
      // The official SecuriThings logo lockups from the Brand Hub: the
      // on-light version for the light theme and the on-dark version for the
      // dark theme. Its size follows --wordmark-height, set in
      // src/styles.css, next to the "Developer Center" text beside it.
      src: {
        light: "/logo-light.svg",
        dark: "/logo-dark.svg",
      },
      alt: "SecuriThings",
      width: "calc(var(--wordmark-height) * 776 / 102)",
      // Link straight to the welcome page. Pointing the logo at "/" makes it
      // depend on the redirect below, which is served as a permanent redirect
      // and is therefore cached by the browser indefinitely.
      href: "/welcome",
    },
  },
  metadata: {
    title: "%s | SecuriThings Developer Center",
    favicon: "/favicon.svg",
    description:
      "Access data, execute tasks, automate device workflows, and integrate with third-party systems using the SecuriThings API.",
  },
  slots: {
    // Zudoku renders either the logo or the site title, never both, so the
    // rest of the name is rendered next to the logo from here. It is styled in
    // src/styles.css to continue the wordmark in the logo.
    "head-navigation-start": (
      <span className="site-wordmark">Developer Center</span>
    ),
  },
  theme: {
    // The SecuriThings brand (Brand Hub, Color v1.4 and Type v1.1). The hex
    // values are listed by name in src/styles.css. Light mode is Snow with Ink
    // text and a Neon Blue accent. Dark mode has Snow text and a Lime accent,
    // the brand pairing for dark backgrounds, on Zudoku's neutral dark
    // background and card colors: Ink reads too blue as a dark-mode surface.
    // Neutrals are used for cards and borders only.
    //
    // `primary` is the link, active and highlight color, including the match
    // highlight in the search dialog and the card hover outline.
    // `accent` and `accentForeground` are the hover, focus and selected colors
    // across the navigation, the page table of contents, dropdowns and the
    // search dialog.
    //
    // The palette has no grey for text, so secondary text is the text color
    // at 70%.
    //
    // Both themes set every key. The generated `:root` block is emitted after
    // Zudoku's own `.dark` block and has equal specificity, so any key left
    // out of the dark theme would inherit the light value.
    fonts: {
      sans: {
        url: "https://fonts.googleapis.com/css2?family=Google+Sans+Flex:wght@300..800&display=swap",
        fontFamily: "Google Sans Flex",
      },
    },
    light: {
      background: "#fafafa", // Snow
      foreground: "#041325", // Ink
      card: "#fafafa", // Snow
      cardForeground: "#041325", // Ink
      popover: "#fafafa", // Snow
      popoverForeground: "#041325", // Ink
      primary: "#2323ff", // Neon Blue
      primaryForeground: "#fafafa", // Snow
      secondary: "#efefef", // Platinum
      secondaryForeground: "#041325", // Ink
      muted: "#efefef", // Platinum
      mutedForeground: "rgb(4 19 37 / 70%)", // Ink at 70%
      accent: "#d6efff", // Pale Blue
      accentForeground: "#2323ff", // Neon Blue
      destructive: "#ff5b57", // Coral
      destructiveForeground: "#041325", // Ink
      border: "#d3d3d3", // Dust Grey
      input: "#d3d3d3", // Dust Grey
      ring: "#2323ff", // Neon Blue
    },
    dark: {
      background: "oklch(0.141 0.005 285.823)", // Zudoku default
      foreground: "#fafafa", // Snow
      card: "oklch(0.21 0.006 285.885)", // Zudoku default
      cardForeground: "#fafafa", // Snow
      popover: "oklch(0.21 0.006 285.885)", // Zudoku default
      popoverForeground: "#fafafa", // Snow
      primary: "#ccff00", // Lime
      primaryForeground: "#041325", // Ink
      secondary: "#353e43", // Jet Black
      secondaryForeground: "#fafafa", // Snow
      muted: "#353e43", // Jet Black
      mutedForeground: "rgb(250 250 250 / 70%)", // Snow at 70%
      accent: "#272757", // Midnight Blue
      accentForeground: "#ccff00", // Lime
      destructive: "#ff5b57", // Coral
      destructiveForeground: "#041325", // Ink
      border: "#353e43", // Jet Black
      input: "#353e43", // Jet Black
      ring: "#ccff00", // Lime
    },
  },
  search: {
    type: "pagefind",
  },
  sitemap: {
    siteUrl: "https://developer.securithings.com",
    // Internal schema dump generated by the OpenAPI plugin; not a page we want
    // search engines to index.
    exclude: ["/api/~schemas"],
  },
  navigation: [
    {
      type: "category",
      label: "Documentation",
      items: [
        { type: "filter", placeholder: "Filter documentation" },
        { type: "section", label: "Getting Started" },
        "welcome",
        "guides/authentication",
        { type: "section", label: "API Fundamentals" },
        "guides/requests-and-responses",
        "guides/pagination-and-filtering",
        "guides/errors",
        "guides/rate-limits",
        "guides/async-tasks",
        { type: "section", label: "Use Cases" },
        "use-cases/device-inventory",
        "use-cases/monitoring",
        "use-cases/device-remediation",
        { type: "section", label: "Resources" },
        "resources/postman",
      ],
    },
    {
      type: "category",
      label: "MCP",
      // Highlights the MCP Server in the top navigation.
      icon: "sparkles",
      items: [
        { type: "doc", file: "mcp/welcome", label: "Welcome" },
        "mcp/get-started",
        {
          type: "category",
          label: "Skills Library",
          link: { type: "doc", file: "mcp/skills-library" },
          // One page per skill, generated by scripts/build-skills.mjs from the
          // folders in skills/. Adding a skill needs no change here.
          items: skills.map((skill) => `mcp/skills/${skill.name}`),
        },
      ],
    },
    { type: "link", to: "/api", label: "API Reference" },
    {
      type: "category",
      label: "Recipes",
      link: { type: "doc", file: "recipes/index", path: "/recipes" },
      items: [
        "recipes/authentication",
        "recipes/retrieve-offline-devices",
        "recipes/onboard-discovered-devices",
        "recipes/restart-devices",
        "recipes/rotate-passwords",
        "recipes/rotate-ssl-certificates",
        "recipes/upgrade-firmware",
      ],
    },
    { type: "doc", file: "changelog", label: "Changelog" },
  ],
  redirects: [
    { from: "/", to: "/welcome" },
    { from: "/introduction", to: "/welcome" },
    { from: "/news", to: "/changelog" },
    {
      from: "/guides/responses-and-errors",
      to: "/guides/requests-and-responses",
    },
    {
      from: "/how-to/retrieve-offline-devices",
      to: "/recipes/retrieve-offline-devices",
    },
    { from: "/how-to/retrieve-eos-devices", to: "/recipes" },
    { from: "/mcp/introduction", to: "/mcp/welcome" },
  ],
  apis: [
    {
      type: "file",
      input: "openapi/root.yaml",
      path: "/api",
      options: {
        showInfoPage: false,
        // Zudoku defaults this to true, which renders every tag group in the
        // reference sidebar expanded. Closed by default; the group for the
        // current page opens itself.
        expandAllTags: false,
      },
    },
  ],
  navigationRules: [{ type: "remove", match: "Schemas" }],
  mdx: {
    components: {
      McpHero,
      Recipe,
      SkillsLibrary,
      SkillOverview,
      SkillPrompts,
      SkillServers,
    },
  },
  docs: {
    files: "/pages/**/*.{md,mdx}",
    publishMarkdown: true,
    defaultOptions: {
      showLastModified: false,
    },
    llms: {
      llmsTxt: true,
      llmsTxtFull: true,
    },
  },
};

export default config;
